'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface InvitationActionResult {
  message: string;
  invitationId: string;
}

/**
 * Hook for re-sending a pending organization invitation.
 * POST /api/v1/organization/invitations/{invitationId}/deliveries
 *
 * Resend goes through Clerk (revoke + recreate, since Clerk has no resend endpoint) so
 * the invitee gets a fresh ticket. Keyed by the provider (Clerk) invitation id; the
 * member-row + seat reconciliation are handled by the backend webhooks, so the client
 * just invalidates the lists on settle.
 */
export function useResendInvitation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<InvitationActionResult, Error, string>({
    mutationFn: async (invitationId: string) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.POST(
        '/api/v1/organization/invitations/{invitationId}/deliveries',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            path: {
              invitationId: invitationId,
            },
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to resend invitation');
      }

      return {
        message: data.message,
        invitationId: data.invitationId,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-invitations'],
      });
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
